import type { ForageKind, ForageSkill, FrenzyStatus } from "../api/forage";
import { ItemSprite } from "./ItemSprite";
import { ForageFrenzyButton } from "./ForageFrenzyButton";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { KIND_ITEM_ID, KIND_LABELS } from "./forageSprites";

interface Props {
  kind: ForageKind;
  skill: ForageSkill | null;
  sessionXp: number;
  count: number;
  frenzy: FrenzyStatus | null;
  onFrenzy: () => void;
}

// Right-rail panel on the foraging page. Three stacked cards: the
// SKILL card (level + XP bar + this-session credit), the FRENZY card
// (the ripening-boost button + loading bar), and the FORAGED ITEMS
// card (running tally of harvests for the current session — resets on
// session end / page nav).
export function ForageSidePanel({
  kind,
  skill,
  sessionXp,
  count,
  frenzy,
  onFrenzy,
}: Props) {
  const theme = pixelTheme;
  const xpPct =
    skill && skill.xp_to_next > 0
      ? Math.max(
          0,
          Math.min(100, (skill.xp_into_level / skill.xp_to_next) * 100),
        )
      : 0;

  return (
    <aside style={{ display: "grid", gap: 16 }}>
      <div style={{ ...simplePanelStyle(theme), padding: "14px 16px" }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.16em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          SKILL
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginTop: 6,
          }}
        >
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 11,
              letterSpacing: "0.08em",
              color: theme.ink,
              textTransform: "uppercase",
            }}
          >
            FORAGING
          </div>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 28,
              letterSpacing: "0.06em",
              color: theme.accent,
            }}
          >
            {skill?.level ?? 1}
          </div>
        </div>
        <div
          title={`${skill?.xp_into_level ?? 0} / ${skill?.xp_to_next ?? 0} XP`}
          style={{
            position: "relative",
            marginTop: 10,
            height: 14,
            background: E32.plum,
            border: `2px solid ${theme.ink}`,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              right: "auto",
              width: `${xpPct}%`,
              background: E32.forest,
              transition: "width 240ms ease-out",
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: theme.inkDim,
            letterSpacing: "0.08em",
          }}
        >
          <span>{(skill?.xp ?? 0).toLocaleString()} XP</span>
          <span>NEXT · {(skill?.xp_to_next ?? 0).toLocaleString()}</span>
        </div>
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: `1px dashed ${theme.panelEdge}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
          }}
        >
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 9,
              letterSpacing: "0.16em",
              color: theme.inkFaint,
              textTransform: "uppercase",
            }}
          >
            THIS SESSION
          </span>
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 16,
              color: E32.forest,
              letterSpacing: "0.06em",
            }}
          >
            +{sessionXp} XP
          </span>
        </div>
      </div>

      <ForageFrenzyButton status={frenzy} onFrenzy={onFrenzy} />

      <div style={{ ...simplePanelStyle(theme), padding: "14px 16px" }}>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            letterSpacing: "0.16em",
            color: theme.inkFaint,
            textTransform: "uppercase",
          }}
        >
          FORAGED ITEMS
        </div>
        {count === 0 ? (
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: 16,
              color: theme.inkFaint,
              padding: "8px 4px",
            }}
          >
            Nothing yet. Click a ripe {KIND_LABELS[kind].toLowerCase()}.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "32px 1fr auto",
              alignItems: "center",
              gap: 10,
              padding: "6px 8px",
              background: E32.parchment,
              border: `2px solid ${theme.panelEdge}`,
              marginTop: 10,
            }}
          >
            <ItemSprite id={KIND_ITEM_ID[kind]} size={24} />
            <div
              style={{
                fontFamily: theme.fontBody,
                fontSize: 16,
                color: theme.ink,
                lineHeight: 1,
              }}
            >
              {KIND_LABELS[kind]}
            </div>
            <div
              style={{
                fontFamily: theme.fontDisplay,
                fontSize: 11,
                color: theme.ink,
                letterSpacing: "0.06em",
              }}
            >
              × {count}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
